/**
 * BrandingProvider — Platform White Label Foundation.
 *
 * Fetch `/api/public/branding` sekali saat App mount, cache di React context.
 * Semua komponen dapat pakai `useBranding()` untuk akses:
 *   - branding.general.name / short_name / tagline / description
 *   - branding.visual.logo / favicon / og_image / default_banner
 *   - branding.theme.primary / secondary / accent / background / text
 *   - branding.contact.whatsapp / email / instagram / facebook / tiktok / youtube / address / maps_url
 *   - branding.footer.text / copyright / powered_by
 *
 * Effects saat branding loaded:
 *   1. Inject CSS variables ke :root — --brand-primary, --brand-secondary, dll
 *   2. Update <title> ke branding.general.name (default fallback)
 *   3. Update favicon link href
 *   4. Update meta theme-color
 *
 * Fallback: kalau fetch gagal, pakai DEFAULT_BRANDING lokal generik (netral)
 * supaya UI tetap render tanpa identitas brand yang salah.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { useHost } from "@/context/HostContext";

const DEFAULT_BRANDING = {
  general: {
    name: "",
    short_name: "",
    tagline: "",
    description: "",
  },
  visual: {
    logo: "/brand/logo-horizontal.svg",
    logo_dark: "",
    favicon: "/favicon.ico",
    og_image: "",
    default_banner: "",
  },
  theme: {
    primary: "#E85D04",
    secondary: "#4A2E1F",
    accent: "#FFB703",
    background: "#FAF9F6",
    text: "#0E0E0E",
    font_heading: "",
    font_body: "",
    button_style: "",
  },
  contact: {
    whatsapp: "", email: "", instagram: "", facebook: "",
    tiktok: "", youtube: "", address: "", maps_url: "",
  },
  footer: { text: "", copyright: "", powered_by: "" },
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: true,
  reload: async () => {},
});

function applyThemeVariables(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", theme.primary);
  root.style.setProperty("--brand-secondary", theme.secondary);
  root.style.setProperty("--brand-accent", theme.accent);
  root.style.setProperty("--brand-background", theme.background);
  root.style.setProperty("--brand-text", theme.text);
  // --brand-surface belum di-configure user; default putih agar card tetap terbaca
  root.style.setProperty("--brand-surface", "#FFFFFF");
  // Belt & suspenders: force body bg agar override kelas Tailwind lama (bg-[#FAF9F6], bg-background)
  if (document.body && theme.background) {
    document.body.style.backgroundColor = theme.background;
  }
  applyBrandFonts(theme.font_heading, theme.font_body);
}

// Template typography runtime — load Google Fonts + override heading/body font.
function applyBrandFonts(heading, body) {
  if (typeof document === "undefined") return;
  const fams = [...new Set([heading, body].filter(Boolean))];
  if (!fams.length) return;
  const href = `https://fonts.googleapis.com/css2?${fams.map((f) => `family=${encodeURIComponent(f)}:wght@400;500;600;700;800`).join("&")}&display=swap`;
  let link = document.getElementById("brand-google-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "brand-google-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) link.href = href;
  let style = document.getElementById("brand-font-style");
  if (!style) {
    style = document.createElement("style");
    style.id = "brand-font-style";
    document.head.appendChild(style);
  }
  const rules = [];
  if (body) rules.push(`body{font-family:'${body}',ui-sans-serif,system-ui,sans-serif;}`);
  if (heading) rules.push(`.font-display{font-family:'${heading}',ui-sans-serif,system-ui,sans-serif !important;}`);
  style.textContent = rules.join("\n");
}

function applyDocumentMeta(branding) {
  if (typeof document === "undefined") return;
  const { general, visual, theme } = branding;
  // Title
  if (general.name) document.title = general.name;
  // Favicon — ganti SEMUA icon link statis (16/32/96/apple-touch dari index.html)
  // agar tidak ada favicon warisan yang menang; tambah cache-busting ringan.
  if (visual.favicon) {
    const bust = visual.favicon.includes("?") ? visual.favicon : `${visual.favicon}?v=b2`;
    document
      .querySelectorAll("link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")
      .forEach((l) => l.parentNode && l.parentNode.removeChild(l));
    const icon = document.createElement("link");
    icon.rel = "icon";
    icon.href = bust;
    document.head.appendChild(icon);
    const apple = document.createElement("link");
    apple.rel = "apple-touch-icon";
    apple.href = bust;
    document.head.appendChild(apple);
  }
  // Theme color
  let themeMeta = document.querySelector("meta[name='theme-color']");
  if (!themeMeta) {
    themeMeta = document.createElement("meta");
    themeMeta.name = "theme-color";
    document.head.appendChild(themeMeta);
  }
  themeMeta.content = theme.primary;
}

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const host = useHost();
  // HOTFIX P0 — Platform Branding Synchronization.
  // Tenant BrandingContext hanya boleh menulis <title>/favicon ketika host
  // sebenarnya bertipe tenant (mis. sinar-telur.dynicty.com). Pada platform
  // host (app.dynicty.com / preview / localhost), Platform Owner Portal
  // memakai identitas Dynicty via PlatformBrandingContext — tenant branding
  // TIDAK boleh menimpa title/favicon platform.
  const isTenantHost = host?.kind === "tenant";

  const load = useCallback(async () => {
    try {
      const r = await api.get("/public/branding");
      if (r.data && typeof r.data === "object") {
        setBranding(r.data);
      }
    } catch { /* keep defaults on failure */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Apply CSS vars whenever branding changes. Document meta (title/favicon)
  // dilewati bila bukan tenant host — mencegah "Sinar Telur" nyasar ke tab
  // Platform Owner Portal.
  useEffect(() => {
    applyThemeVariables(branding.theme);
    if (isTenantHost) applyDocumentMeta(branding);
  }, [branding, isTenantHost]);

  const value = useMemo(() => ({ branding, loading, reload: load }), [branding, loading, load]);
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  return useContext(BrandingContext);
}

/** Convenience hooks for common fields */
export function useBrandName() {
  return useContext(BrandingContext).branding.general.name;
}
